import { useState } from 'react';
import styled from 'styled-components';
import {
  ContainerMarginSections,
  DisplayTitle,
  SectionLabel,
  ScriptAccent,
  BodyText,
  Colors,
  fonts,
} from '../style/GlobalStyles';

const Grid = styled.div`
  display: flex;
  gap: 3rem;
  align-items: flex-start;

  @media (max-width: 900px) {
    flex-direction: column;
    gap: 2rem;
  }
`;

const Left = styled.div`
  flex: 0 0 360px;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  @media (max-width: 900px) {
    flex: 0 0 auto;
  }
`;

const Nav = styled.div`
  display: flex;
  gap: 0.8rem;
  margin-top: 1rem;
`;

const ArrowBtn = styled.button`
  width: 52px;
  height: 52px;
  border-radius: 50%;
  border: 1.5px solid ${Colors.ink};
  background: ${({ $filled }) => ($filled ? Colors.ink : 'transparent')};
  color: ${({ $filled }) => ($filled ? Colors.paper : Colors.ink)};
  font-size: 1.1rem;
  cursor: pointer;
  transition: transform 0.2s;

  &:hover {
    transform: translateY(-2px);
  }
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const Scribble = styled(ScriptAccent)`
  margin-top: 1rem;
`;

const Viewport = styled.div`
  flex: 1;
  overflow: hidden;
`;

const Track = styled.div`
  display: flex;
  gap: 1.5rem;
  transition: transform 0.5s cubic-bezier(0.65, 0, 0.35, 1);
  transform: translateX(calc(-${({ $index }) => $index} * (360px + 1.5rem)));
`;

const CertCard = styled.div`
  flex: 0 0 360px;
  min-height: 380px;
  background: ${Colors.paper};
  border: 1.5px solid ${Colors.ink};
  border-radius: 22px;
  padding: 2.2rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  transition: transform 0.3s ease, background 0.3s ease;

  &:hover {
    transform: translateY(-6px);
  }

  &:hover .badge {
    background: ${Colors.lime};
    border-color: ${Colors.lime};
  }

  @media (max-width: 480px) {
    flex-basis: 280px;
    min-height: 320px;
    padding: 1.6rem;
  }
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Badge = styled.span`
  font-family: ${fonts.body};
  font-size: 0.8rem;
  font-weight: 600;
  color: ${Colors.ink};
  border: 1.5px solid ${Colors.ink};
  border-radius: 999px;
  padding: 0.4rem 1rem;
  transition: background 0.3s ease, border-color 0.3s ease;
`;

const Seal = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1.5px solid ${Colors.ink};
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${fonts.display};
  font-size: 0.95rem;
  color: ${Colors.ink};
`;

const CertTitle = styled.h3`
  font-family: ${fonts.display};
  text-transform: uppercase;
  font-weight: 400;
  font-size: clamp(1.8rem, 3vw, 2.4rem);
  line-height: 0.98;
  color: ${Colors.ink};
  margin: 2.5rem 0 0 0;
`;

const Foot = styled.div`
  border-top: 1px solid ${Colors.line};
  padding-top: 1.4rem;
  margin-top: 2rem;
  display: flex;
  align-items: baseline;
  justify-content: space-between;

  .issuer {
    font-family: ${fonts.body};
    font-weight: 600;
    font-size: 1.05rem;
    color: ${Colors.ink};
  }
  .year {
    font-family: ${fonts.body};
    font-size: 0.95rem;
    color: ${Colors.muted};
  }
`;

const Certificates = ({ certificates = [] }) => {
  const [index, setIndex] = useState(0);
  const maxIndex = Math.max(0, certificates.length - 1);

  return (
    <ContainerMarginSections>
      <Grid>
        <Left>
          <SectionLabel className="gsap-fade-up">Certificates</SectionLabel>
          <DisplayTitle className="gsap-fade-up">
            Trusted
            <br />
            credentials
          </DisplayTitle>
          <BodyText className="gsap-fade-up">
            Certifications and training that back the work and keep quality
            consistent.
          </BodyText>
          <Nav className="gsap-fade-up">
            <ArrowBtn
              onClick={() => setIndex((i) => Math.max(0, i - 1))}
              disabled={index === 0}
              aria-label="Previous"
            >
              ←
            </ArrowBtn>
            <ArrowBtn
              $filled
              onClick={() => setIndex((i) => Math.min(maxIndex, i + 1))}
              disabled={index >= maxIndex}
              aria-label="Next"
            >
              →
            </ArrowBtn>
          </Nav>
          <Scribble className="gsap-fade-up">Proof in black &amp; white</Scribble>
        </Left>

        <Viewport className="gsap-fade-up">
          <Track $index={index}>
            {certificates.map((c, i) => (
              <CertCard key={c.title}>
                <div>
                  <TopRow>
                    <Badge className="badge">{c.code}</Badge>
                    <Seal>{String(i + 1).padStart(2, '0')}</Seal>
                  </TopRow>
                  <CertTitle>{c.title}</CertTitle>
                </div>
                <Foot>
                  <span className="issuer">{c.issuer}</span>
                  <span className="year">{c.year}</span>
                </Foot>
              </CertCard>
            ))}
          </Track>
        </Viewport>
      </Grid>
    </ContainerMarginSections>
  );
};

export default Certificates;
